import { type EnsSelectArray, EnsSelectStat, Quantile, Spread, quantileBandList } from "@/utility/ensemble";
import { t } from "@lingui/core/macro";
import { MmEnsdropdown } from "@mm/zeus-react";
import { difference } from "lodash";
import { createContext, useContext, useMemo } from "react";
import styles from "./style.module.scss";

const QUANTILES = Object.values(Quantile);

interface EnsSelectionPanelContextType {
  availableValues: {
    bands?: EnsSelectArray;
    spread: EnsSelectArray;
    stat: EnsSelectArray;
    quantiles: EnsSelectArray;
    members: EnsSelectArray;
  };

  selectedEns: EnsSelectArray;

  allMembersSelected: boolean;
  allQuantilesSelected: boolean;

  onChange?: (value: EnsSelectArray) => void;
}

const EnsSelectionPanelContext = createContext<EnsSelectionPanelContextType>({
  availableValues: {
    spread: [],
    stat: [],
    quantiles: [],
    members: [],
  },
  selectedEns: [],

  allMembersSelected: false,
  allQuantilesSelected: false,
});

type MultipleEnsSelectionProps = {
  memberList: EnsSelectArray;
  quantiles?: EnsSelectArray;
  selected: EnsSelectArray;
  onChange: (v: EnsSelectArray) => void;
  custom?: boolean;
  grouped?: boolean;
};

const MultipleEnsSelection = ({
  memberList,
  selected,
  onChange,
  quantiles = QUANTILES,
  custom,
}: MultipleEnsSelectionProps) => {
  const allMembersSelected = useMemo(() => difference(memberList, selected).length === 0, [memberList, selected]);
  const allQuantilesSelected = useMemo(() => difference(quantiles, selected).length === 0, [selected]);

  const handleSelectionChanged = (
    event: CustomEvent<{ members: { id: string }[]; quantiles: { id: string }[]; others: { id: string }[] }>,
  ) => {
    const { members, quantiles, others } = event.detail;
    onChange([...members.map((m) => m.id), ...quantiles.map((q) => q.id), ...others.map((o) => o.id)]);
  };

  return (
    <EnsSelectionPanelContext.Provider
      value={{
        availableValues: {
          bands: quantileBandList,
          spread: custom ? [] : [...Object.values(Spread)],
          stat: custom ? [] : [...Object.values(EnsSelectStat)],
          quantiles: quantiles,
          members: memberList,
        },
        selectedEns: selected,

        onChange,

        allMembersSelected,
        allQuantilesSelected,
      }}
    >
      <div className={styles["ens-selection-panel"]}>
        <MmEnsdropdown
          placeholder={t`Select...`}
          members={memberList.map((member) => ({ id: member, title: member }))}
          quantiles={quantiles.map((quantile) => ({ id: quantile, title: quantile }))}
          others={[
            ...(!custom ? Object.values(Spread).map((spread) => ({ id: spread, title: spread })) : []),
            ...(!custom ? Object.values(EnsSelectStat).map((stat) => ({ id: stat, title: stat })) : []),
          ]}
          selected={selected}
          onSelectionChanged={handleSelectionChanged}
        />
      </div>
    </EnsSelectionPanelContext.Provider>
  );
};

const useEnsSelectionContext = () => useContext(EnsSelectionPanelContext);

export { MultipleEnsSelection, useEnsSelectionContext };
